import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

export default function Assessment() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/assessment").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-sm text-slate-400">Loading assessment…</div>;

  if (!data.training_complete && !data.passed) {
    return (
      <Card className="mx-auto max-w-xl text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100"><Lock className="h-6 w-6 text-slate-400" /></div>
        <h2 className="mt-4 text-lg font-bold text-slate-900">Assessment locked</h2>
        <p className="mt-2 text-sm text-slate-500">Finish every training module to unlock the {data.threshold}%-to-pass assessment.</p>
        <Button onClick={() => navigate("/training")} className="btn-primary-gradient mt-6 border-0 text-white">Go to Training</Button>
      </Card>
    );
  }

  const submit = async () => {
    if (Object.keys(answers).length < data.questions.length) {
      toast({ title: "Answer all questions", description: "Please respond to every question.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/assessment/submit", { answers });
      setResult(r.data);
      if (r.data.passed) {
        toast({ title: "You passed!", description: `Score ${r.data.score}% — you're now certified.` });
      } else {
        toast({ title: "Not quite", description: `Score ${r.data.score}%. You need ${data.threshold}%.`, variant: "destructive" });
      }
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <Card className="mx-auto max-w-xl text-center">
        <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${result.passed ? "bg-emerald-100" : "bg-rose-100"}`}>
          <CheckCircle2 className={`h-7 w-7 ${result.passed ? "text-emerald-600" : "text-rose-500"}`} />
        </div>
        <h2 className="mt-4 text-2xl font-extrabold text-slate-900">{result.score}%</h2>
        <p className="mt-1 text-sm text-slate-500">{result.correct} of {result.total} correct — {result.passed ? "passed" : `need ${result.threshold}%`}</p>
        {result.passed ? (
          <Button onClick={() => navigate("/certification")} className="btn-primary-gradient mt-6 border-0 text-white">View your certificate</Button>
        ) : (
          <Button onClick={() => { setResult(null); setAnswers({}); }} className="btn-primary-gradient mt-6 border-0 text-white">Retake assessment</Button>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-base font-bold text-slate-900">Final assessment</h2>
        <p className="text-sm text-slate-500">{data.questions.length} questions — score {data.threshold}% or higher to become certified.</p>
      </Card>
      {data.questions.map((q, idx) => (
        <Card key={q.id}>
          <div className="font-semibold text-slate-900">{idx + 1}. {q.question}</div>
          <RadioGroup className="mt-3 space-y-2" value={answers[q.id]?.toString()} onValueChange={(v) => setAnswers((a) => ({ ...a, [q.id]: parseInt(v) }))}>
            {q.options.map((opt, i) => (
              <label key={i} htmlFor={`${q.id}-${i}`} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-100 p-3 hover:bg-slate-50">
                <RadioGroupItem id={`${q.id}-${i}`} value={i.toString()} />
                <span className="text-sm text-slate-700">{opt}</span>
              </label>
            ))}
          </RadioGroup>
        </Card>
      ))}
      <Button disabled={busy} onClick={submit} className="btn-primary-gradient border-0 text-white">{busy ? "Submitting…" : "Submit assessment"}</Button>
    </div>
  );
}
